import { Observable } from 'rxjs';

import {
	AutomixMicOptimizationMode,
	AutomixNoiseDetectionSensitivity,
	AutomixNoiseRemovalLevel,
	DeviceConferenceOperationMode,
	DirectOutputTapPoint,
	Response
} from '@shure/shared/angular/data-access/system-api/models';

import {
	IntellimixDevice,
	IntellimixAutomixChannel,
	IntellimixInputChannel,
	IntellimixBasicAutomixChannel
} from './models';
/**
 * `IntellimixApiService` is an abstraction for **all** the functionality required
 * by the intellimix feature.
 */
export abstract class IntellimixApiService {
	/**
	 * Get intellimix device by id.
	 */
	public abstract getDevice$(deviceId: string): Observable<IntellimixDevice>;

	public abstract getIntellimixBasicAutomixChannel$(channelId: string): Observable<IntellimixBasicAutomixChannel>;

	public abstract getIntellimixAutomixChannel$(channelId: string): Observable<IntellimixAutomixChannel>;

	public abstract getIntellimixInputChannel$(channelId: string): Observable<IntellimixInputChannel>;

	public abstract updateChannelName(channelId: string, name: string): Observable<Response<void, string>>;

	public abstract setAutomixMode(channelId: string, automixMode: string): Observable<Response<void, string>>;

	public abstract setSendToMix(channelId: string, sendToMixbusEnabled: boolean): Observable<Response<void, string>>;

	public abstract setAutomixGainMeterEnabled(deviceId: string, status: boolean): Observable<Response<void, string>>;

	public abstract setVirtualAcousticBoundaryEnabled(
		deviceId: string,
		isEnabled: boolean
	): Observable<Response<void, string>>;

	public abstract setVirtualAcousticBoundarySensitivity(
		deviceId: string,
		sensitivity: number
	): Observable<Response<void, string>>;

	public abstract updateDeviceOperationMode(
		deviceId: string,
		mode: DeviceConferenceOperationMode
	): Observable<Response<void, string>>;

	public abstract setMaxOpenChannels(channelId: string, numChannels: number): Observable<Response<void, string>>;

	public abstract setMicOptimizationMode(
		channelId: string,
		mode: AutomixMicOptimizationMode
	): Observable<Response<void, string>>;

	public abstract setKeepLastChannelActive(channelId: string, status: boolean): Observable<Response<void, string>>;

	public abstract setOffAttenuation(channelId: string, decibels: number): Observable<Response<void, string>>;

	public abstract setHoldTime(channelId: string, holdTime: number): Observable<Response<void, string>>;

	public abstract setGatingSensitivity(channelId: string, sensitivity: number): Observable<Response<void, string>>;

	public abstract setAutomixSoloMode(
		automixChannelId: string,
		soloChannelId?: string
	): Observable<Response<void, string>>;

	public abstract setPostGateSoloMode(channelId: string, isEnabled: boolean): Observable<Response<void, string>>;

	public abstract setPriorityMode(channelId: string, priorityEnabled: boolean): Observable<Response<void, string>>;

	public abstract setAlwaysOnMode(channelId: string, alwaysOnEnabled: boolean): Observable<Response<void, string>>;

	public abstract setMuteMode(channelId: string, muted: boolean): Observable<Response<void, string>>;

	public abstract updateChannelGain(channelId: string, gain: number): Observable<Response<void, string>>;

	public abstract setPostGateMuteMode(channelId: string, muted: boolean): Observable<Response<void, string>>;

	public abstract setDirectOutputTapPoint(
		channelId: string,
		tapPoint: DirectOutputTapPoint
	): Observable<Response<void, string>>;

	public abstract updateChannelPostGateGain(channelId: string, gain: number): Observable<Response<void, string>>;

	public abstract resetAutomixerDefaults(channelId: string): Observable<Response<void, string>>;

	public abstract setAutomixerEnabled(channelId: string, isEnabled: boolean): Observable<Response<void, string>>;

	public abstract setNoiseDetectionSensitivity(
		channelId: string,
		value: AutomixNoiseDetectionSensitivity
	): Observable<Response<void, string>>;

	public abstract setNoiseRemovalLevel(
		channelId: string,
		value: AutomixNoiseRemovalLevel
	): Observable<Response<void, string>>;
}
